import { AdminMessage } from "@components/admin/admin-helpers"
import { FadeScroller } from "@components/atoms/FadeScroller"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { Dialog } from "@components/ui/Dialog"
import { alerts } from "@features/alerts/alerts"
import { NewsletterSubscribe } from "@features/newsletter/NewsletterSubscribe"
import { formatTffCenterStatus } from "@features/tff/tff-utils"
import { TFFProgram } from "@features/tff/TFFProgram"
import { cn } from "@utils/tw"
import { ContentBlock, defineBlock, EditableText, SlotBlocks, usePostTitleEditor } from "eddev/blocks"
import { PropsWithChildren, useMemo, useState } from "react"

export const meta: BlockMeta = {
  title: "TFF Program",
  icon: "location",
  flags: {
    fullWidth: true,
  },
  postMetaBlock: {
    fieldName: "centerInfo",
    postTypes: ["tff-center"],
  },
}

export default defineBlock("tff-center/tff-center-program", (props) => {
  return (
    <div>
      <ProgramHeader {...props} />

      {props.showFilmTiles ? (
        <>
          <ProgramSection adminLabel="Live Program">
            <TFFProgram program={props.tffCenter!} />
          </ProgramSection>
        </>
      ) : (
        <>
          <ProgramSection adminLabel="Off-Season Content">
            <OffSeasonContent {...props} />
          </ProgramSection>
        </>
      )}

      <ProgramSection adminLabel="Location Sponsors">
        <SlotBlocks
          id="program-footer"
          title="Center Sponsors"
          template={[["tff-center/tff-center-sponsors", {}]]}
          templateLock="all"
        />
      </ProgramSection>

      {env.admin && (
        <ProgramSection adminLabel="Tickets Page">
          <SlotBlocks
            id="tickets-page"
            title="Ticketing Page"
            headerTemplate={[["tff-center/tff-center-tickets-header", {}]]}
            allowedBlocks={["#richtext", "#subpage", "#subpage-layout"]}
            adminClassName="flex flex-col min-h-10"
            defaultBlocks={[
              ["page/page-siderail", {}],
              ["core/paragraph", {}],
            ]}
            appender={{ type: "button" }}
          />
        </ProgramSection>
      )}
    </div>
  )
})

function ProgramSection(props: PropsWithChildren<{ adminLabel: string }>) {
  if (env.admin) {
    return (
      <div className="mt-9 outline outline-2 outline-bg-mid relative">
        <div className="bg-bg-mid text-fg type-label-caps absolute bottom-full left-grid-margin px-3 py-2">
          {props.adminLabel}
        </div>
        <div>{props.children}</div>
      </div>
    )
  } else {
    return props.children
  }
}

function ProgramHeader(props: BlockProps["tff-center/tff-center-program"]) {
  const status = formatTffCenterStatus(props)

  return (
    <div className="w-full h-[50vw] max-h-[calc(100vh-var(--spacing-headerHeight)-100px)] min-h-[30vw] bg-black text-white relative isolate">
      <ResponsiveImage {...props.headerImage} max className="z-0 opacity-70" />
      <div className="pt-5 h-full flex flex-col gap-10 justify-between relative z-20">
        <div className="w-grid mx-auto flex flex-col gap-3">
          <EditableText as="h1" className="type-title-xxl" store={usePostTitleEditor(props.tffCenter?.title!)} />
          <div className="type-body-l">
            {status.datesText && <p>{status.datesText}</p>}
            {status.venueName && <p>{status.venueName}</p>}
            {status.saleStatusLabel && (
              <Chip shape="rounded" variant="hero">
                {status.saleStatusLabel}
              </Chip>
            )}
          </div>
        </div>
        <div className="sm:w-grid sm:mx-auto overflow-auto scrollbars-hidden">
          <div className="max-sm:px-grid-margin flex gap-3 sm:grid max-sm:w-min grid-cols-3 sm:gap-grid-gutter pb-5 max-sm:[&_>_*]:flex-none">
            <Button href={`/tff/tickets/${props.tffCenter?.slug}`} variant="hero" shape="branded">
              Subscriptions & Flexipasses
            </Button>
            <Button href="/tff/about/" variant="hero" shape="branded">
              About TFF
            </Button>
            <Button href="/tff/support/" variant="hero" shape="branded">
              Support TFF
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function OffSeasonContent(props: BlockProps["tff-center/tff-center-program"]) {
  const status = formatTffCenterStatus(props)
  const [newsletterOpen, setNewsletterOpen] = useState(false)

  return (
    <>
      <div className="grid-auto py-6">
        <div className="col-span-12 md:col-span-6 flex flex-col gap-y-3 sm:items-start">
          <div className="type-title-m">{status.datesText}</div>
          <Button onClick={() => setNewsletterOpen(true)} rightIcon="chevronRight">
            Stay up to date
          </Button>
          <Dialog
            title="Don't miss a thing"
            description={`Sign up to our newsletter to stay up-to-date on TFF in ${props.tffCenter?.title}.`}
            open={newsletterOpen}
            onDismiss={() => setNewsletterOpen(false)}
            onOpenChange={setNewsletterOpen}
            className="max-w-[350px]"
          >
            <NewsletterSubscribe
              dialog
              defaultNewsletter={props.tffCenter?.title!}
              onDismiss={() => setNewsletterOpen(false)}
              onSuccess={() => {
                setNewsletterOpen(false)
                alerts.displayAlert({
                  title: "You're on the list!",
                  description: "Thanks for signing up.",
                  confirmButton: "Done",
                })
              }}
            />
          </Dialog>
        </div>
        <div className="col-span-6 flex flex-col">
          <SlotBlocks
            title="Off Season"
            id="off-season"
            defaultBlocks={[["core/paragraph", { content: "" }]]}
            allowedBlocks={["#richtext"]}
            appender={{ type: "default" }}
            adminClassName="flex flex-col"
            wrapBlock={(children, ctx) => {
              const marginClass = ctx.current.flags?.marginRule && "block-margins-" + ctx.current.flags.marginRule
              return <div className={cn(marginClass)}>{children}</div>
            }}
          />
        </div>
      </div>
    </>
  )
}
